import React from 'react';
import Icon from '../AppIcon';

const ProgressIndicator = ({ 
  currentStep = 1, 
  totalSteps = 3, 
  steps = [],
  onStepClick,
  className = ""
}) => {
  const defaultSteps = [
    { id: 1, label: 'Setup', description: 'Configure your planner' },
    { id: 2, label: 'Preview', description: 'Review your design' },
    { id: 3, label: 'Generate', description: 'Download your planner' }
  ];

  const stepItems = steps?.length > 0 ? steps : defaultSteps;

  const getStepStatus = (stepNumber) => {
    if (stepNumber < currentStep) return 'completed';
    if (stepNumber === currentStep) return 'current';
    return 'upcoming';
  };

  const isClickable = (stepNumber) => {
    return onStepClick && stepNumber <= currentStep;
  };

  const handleStepClick = (stepNumber) => {
    if (isClickable(stepNumber)) {
      onStepClick(stepNumber);
    }
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Desktop Progress Indicator */}
      <div className="hidden md:block">
        <div className="flex items-center justify-between">
          {stepItems?.map((step, index) => {
            const stepNumber = step?.id || index + 1;
            const status = getStepStatus(stepNumber);
            const isLast = index === stepItems?.length - 1;

            return (
              <div key={stepNumber} className="flex items-center flex-1">
                {/* Step Circle and Content */}
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => handleStepClick(stepNumber)}
                    disabled={!isClickable(stepNumber)}
                    className={`
                      relative w-10 h-10 rounded-full border-2 flex items-center justify-center
                      transition-all duration-300 hover:scale-105
                      ${status === 'completed'
                        ? 'bg-success border-success text-success-foreground'
                        : status === 'current' ?'bg-primary border-primary text-primary-foreground animate-pulse-gentle' :'bg-card border-border text-muted-foreground'
                      }
                      ${isClickable(stepNumber) ? 'cursor-pointer hover:shadow-md' : 'cursor-default'}
                    `}
                  >
                    {status === 'completed' ? (
                      <Icon name="Check" size={16} />
                    ) : (
                      <span className="font-heading font-semibold text-sm">
                        {stepNumber}
                      </span>
                    )}
                  </button>
                  
                  <div className="mt-3 text-center">
                    <div className={`
                      font-heading font-semibold text-sm
                      ${status === 'current' ? 'text-primary' : 'text-foreground'}
                    `}>
                      {step?.label}
                    </div>
                    <div className="font-caption text-xs text-muted-foreground mt-1">
                      {step?.description}
                    </div>
                  </div>
                </div>
                {/* Connector Line */}
                {!isLast && (
                  <div className="flex-1 mx-4 h-0.5 bg-border relative">
                    <div 
                      className={`
                        h-full transition-all duration-500
                        ${stepNumber < currentStep ? 'bg-success' : 'bg-border'}
                      `}
                      style={{
                        width: stepNumber < currentStep ? '100%' : '0%'
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {/* Mobile Progress Indicator */}
      <div className="md:hidden">
        <div className="bg-card rounded-lg p-4 border border-border">
          {/* Progress Bar */}
          <div className="flex items-center justify-between mb-4">
            <span className="font-caption text-sm text-muted-foreground">
              Step {currentStep} of {totalSteps}
            </span>
            <span className="font-caption text-sm text-muted-foreground">
              {Math.round((currentStep / totalSteps) * 100)}%
            </span>
          </div>
          
          <div className="w-full bg-muted rounded-full h-2 mb-4">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-500"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>

          {/* Current Step Info */}
          <div className="flex items-center space-x-3">
            <div className={`
              w-8 h-8 rounded-full border-2 flex items-center justify-center
              ${getStepStatus(currentStep) === 'current' ?'bg-primary border-primary text-primary-foreground' :'bg-success border-success text-success-foreground'
              }
            `}>
              {getStepStatus(currentStep) === 'completed' ? (
                <Icon name="Check" size={14} />
              ) : (
                <span className="font-heading font-semibold text-xs">
                  {currentStep}
                </span>
              )}
            </div>
            
            <div>
              <div className="font-heading font-semibold text-sm text-foreground">
                {stepItems?.[currentStep - 1]?.label}
              </div>
              <div className="font-caption text-xs text-muted-foreground">
                {stepItems?.[currentStep - 1]?.description}
              </div>
            </div>
          </div>

          {/* Step Navigation Dots */}
          <div className="flex items-center justify-center space-x-2 mt-4">
            {stepItems?.map((_, index) => {
              const stepNumber = index + 1;
              const status = getStepStatus(stepNumber);
              
              return (
                <button
                  key={stepNumber}
                  onClick={() => handleStepClick(stepNumber)}
                  disabled={!isClickable(stepNumber)}
                  className={`
                    w-2 h-2 rounded-full transition-all duration-200
                    ${status === 'completed' || status === 'current'
                      ? 'bg-primary' :'bg-muted'
                    }
                    ${isClickable(stepNumber) ? 'cursor-pointer hover:scale-125' : 'cursor-default'}
                  `}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressIndicator;